import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  AutocompleteMultiSelect,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@shigo-ui/ui';

import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import { NetworkAvailableFeature } from '../../../../auth/networkFeatures';
import { formatShigoRole } from '../../../../auth/roleLabels';
import { NetworkCostType, NetworkCpmMetric, NetworkTcfEnforcement } from '../../model';
import type { NetworkFormValue } from '../../networkFormSchema';
import { networkConfigurableRoles } from '../../networkRoles';

const noneValue = 'NONE';
const tcfEnforcementLabelKeys: Record<NetworkTcfEnforcement, DashboardMessageKey> = {
  [NetworkTcfEnforcement.Off]: 'networks.tcf.off',
  [NetworkTcfEnforcement.On]: 'networks.tcf.on',
  [NetworkTcfEnforcement.DpOnly]: 'networks.tcf.dpOnly',
};
const featureLabelKeys: Record<NetworkAvailableFeature, DashboardMessageKey> = {
  [NetworkAvailableFeature.Campaign]: 'networks.feature.campaign',
  [NetworkAvailableFeature.AdUnit]: 'networks.feature.adUnit',
  [NetworkAvailableFeature.A2v]: 'networks.feature.a2v',
  [NetworkAvailableFeature.Content]: 'networks.feature.content',
  [NetworkAvailableFeature.Immu]: 'networks.feature.immu',
  [NetworkAvailableFeature.LiveRamp]: 'networks.feature.liveRamp',
  [NetworkAvailableFeature.Email]: 'networks.feature.email',
  [NetworkAvailableFeature.Stitcher]: 'networks.feature.stitcher',
  [NetworkAvailableFeature.BidCache]: 'networks.feature.bidCache',
  [NetworkAvailableFeature.Owner]: 'networks.feature.owner',
};

export function NetworkGeneralFields({ editing }: { editing: boolean }) {
  'use no memo';
  const { control, register } = useFormContext<NetworkFormValue>();
  const costType = useWatch({ control, name: 'saasCostType' });

  return (
    <DashboardPageFormBody width="lg">
      <div className="grid gap-5 md:grid-cols-2">
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('networks.name')}
              htmlFor="network-name"
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                id="network-name"
                required
                disabled={editing}
                aria-invalid={Boolean(fieldState.error)}
              />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="qps"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('networks.qps')}
              htmlFor="network-qps"
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                id="network-qps"
                type="number"
                min="0"
                aria-invalid={Boolean(fieldState.error)}
              />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="saasCostType"
          render={({ field }) => (
            <DashboardPageFormField label={dashboardT('networks.saasType')}>
              <Select
                value={field.value || noneValue}
                onValueChange={(value) => {
                  field.onChange(value === noneValue ? '' : value);
                }}
              >
                <SelectTrigger aria-label={dashboardT('networks.saasType')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={noneValue}>{dashboardT('networks.none')}</SelectItem>
                  <SelectItem value={NetworkCostType.RevenueShare}>
                    {dashboardT('networks.cost.revenueShare')}
                  </SelectItem>
                  <SelectItem value={NetworkCostType.Cpm}>
                    {dashboardT('networks.cost.cpm')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </DashboardPageFormField>
          )}
        />
        {costType === NetworkCostType.RevenueShare ? (
          <Controller
            control={control}
            name="saasRevenueShare"
            render={({ field, fieldState }) => (
              <DashboardPageFormField
                label={dashboardT('networks.revenueShare')}
                htmlFor="network-revenue-share"
                required
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  id="network-revenue-share"
                  type="number"
                  min="0"
                  max="100"
                  required
                  aria-invalid={Boolean(fieldState.error)}
                />
              </DashboardPageFormField>
            )}
          />
        ) : null}
        {costType === NetworkCostType.Cpm ? (
          <>
            <Controller
              control={control}
              name="saasCpmMetric"
              render={({ field, fieldState }) => (
                <DashboardPageFormField
                  label={dashboardT('networks.saasMetric')}
                  required
                  error={fieldState.error?.message}
                >
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      aria-label={dashboardT('networks.saasMetric')}
                      aria-invalid={Boolean(fieldState.error)}
                    >
                      <SelectValue placeholder={dashboardT('networks.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NetworkCpmMetric.AdRequests}>
                        {dashboardT('networks.metric.adRequests')}
                      </SelectItem>
                      <SelectItem value={NetworkCpmMetric.FilledImpressions}>
                        {dashboardT('networks.metric.filledImpressions')}
                      </SelectItem>
                      <SelectItem value={NetworkCpmMetric.AdImpressions}>
                        {dashboardT('networks.metric.adImpressions')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </DashboardPageFormField>
              )}
            />
            <Controller
              control={control}
              name="saasCpmValue"
              render={({ field, fieldState }) => (
                <DashboardPageFormField
                  label={dashboardT('networks.saasValue')}
                  htmlFor="network-cpm-value"
                  required
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    id="network-cpm-value"
                    type="number"
                    min="0"
                    required
                    aria-invalid={Boolean(fieldState.error)}
                  />
                </DashboardPageFormField>
              )}
            />
          </>
        ) : null}
        <DashboardPageFormField
          label={dashboardT('networks.dashboardDomain')}
          htmlFor="network-dashboard-domain"
        >
          <Input id="network-dashboard-domain" {...register('dashboardDomain')} />
        </DashboardPageFormField>
        <DashboardPageFormField
          label={dashboardT('networks.adstxtDomain')}
          htmlFor="network-adstxt-domain"
        >
          <Input id="network-adstxt-domain" {...register('adstxtDomain')} />
        </DashboardPageFormField>
      </div>
      <Controller
        control={control}
        name="features"
        render={({ field }) => (
          <DashboardPageFormField label={dashboardT('networks.allowedFeatures')}>
            <AutocompleteMultiSelect
              label={dashboardT('networks.allowedFeatures')}
              options={Object.values(NetworkAvailableFeature).map((feature) => ({
                id: feature,
                label: dashboardT(featureLabelKeys[feature]),
              }))}
              value={field.value}
              onValueChange={field.onChange}
              placeholder={dashboardT('networks.searchFeatures')}
              emptyMessage={dashboardT('networks.noFeatures')}
            />
          </DashboardPageFormField>
        )}
      />
      <Controller
        control={control}
        name="allowedRoles"
        render={({ field }) => (
          <DashboardPageFormField label={dashboardT('networks.allowedRoles')}>
            <AutocompleteMultiSelect
              label={dashboardT('networks.allowedRoles')}
              options={networkConfigurableRoles.map((role) => ({
                id: role,
                label: formatShigoRole(role),
              }))}
              value={field.value}
              onValueChange={field.onChange}
              placeholder={dashboardT('networks.searchRoles')}
              emptyMessage={dashboardT('networks.noRoles')}
            />
          </DashboardPageFormField>
        )}
      />
      <Controller
        control={control}
        name="tcfEnforcementEnabled"
        render={({ field }) => (
          <DashboardPageFormField label={dashboardT('networks.tcf')}>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={dashboardT('networks.tcf')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(NetworkTcfEnforcement).map((value) => (
                  <SelectItem key={value} value={value}>
                    {dashboardT(tcfEnforcementLabelKeys[value])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DashboardPageFormField>
        )}
      />
      <Controller
        control={control}
        name="skipDefaultNetworkNode"
        render={({ field }) => (
          <div className="border-input flex items-center gap-3 rounded-md border p-4">
            <Switch
              id="network-skip-default-node"
              name={field.name}
              ref={field.ref}
              checked={field.value}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
              aria-describedby="network-skip-default-node-description"
            />
            <div className="space-y-1">
              <Label htmlFor="network-skip-default-node" className="cursor-pointer">
                {dashboardT('networks.skipDefaultNetworkNode')}
              </Label>
              <p
                id="network-skip-default-node-description"
                className="text-muted-foreground text-sm"
              >
                {dashboardT('networks.skipDefaultNetworkNodeDescription')}
              </p>
            </div>
          </div>
        )}
      />
    </DashboardPageFormBody>
  );
}
